import { ToggleRequirementItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/ToggleRequirementItem.tsx';
import {
	KycSection,
	NestedRequirement,
	SectionDivider,
	SectionTitle,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/styles.ts';
import { ENTITY_TABS } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/types.ts';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { MantineTabs } from '@dealstack/frontend/shared/components/MantineTabs/MantineTabs.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { Drawer } from '@mantine/core';
import { useState } from 'react';
import { MantineTabProps } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.types';
import { useInvestmentWorkflowKycSettings } from './useInvestmentWorkflowKycSettings.ts';
import { useKycCertificationEnabled } from '@dealstack/frontend/shared/hooks/useKycCertificationEnabled/useKycCertificationEnabled.hook.ts';

interface Props {
	mipId: string;
	opened: boolean;
	onClose: () => void;
	kycWorkflowId: string | undefined;
	onKycRequiredToggle?: () => void;
}

export const InvestmentWorkflowKycSettingsDrawer = ({
	mipId,
	opened,
	onClose,
	kycWorkflowId,
	onKycRequiredToggle,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const [activeTab, setActiveTab] = useState<ENTITY_TABS>(ENTITY_TABS.INDIVIDUAL);
	const { settings, toggleSetting, isLoading } = useInvestmentWorkflowKycSettings(
		kycWorkflowId,
		mipId
	);
	const { certificationEnabled } = useKycCertificationEnabled({ mipId });

	const tabsConfig: MantineTabProps<ENTITY_TABS>[] = [
		{
			label: tMipManage('kyc_settings.label.individual'),
			value: ENTITY_TABS.INDIVIDUAL,
			icon: 'information',
		},
		{
			label: tMipManage('kyc_settings.label.vehicle'),
			value: ENTITY_TABS.INVESTMENT_VEHICLE,
			icon: 'derived_commuted_structure',
		},
	];

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tMipManage('investments.drawer.kyc_requirements.title')}
			styles={{
				body: {
					height: 'calc(100% - 60px)',
					overflow: 'auto',
				},
			}}
		>
			{isLoading && <InlineLoader />}

			<KycSection $noPadding $noMaxWidth>
				<ToggleRequirementItem
					title={tMipManage('kyc_settings.kyc_required.label')}
					description={tMipManage('kyc_settings.kyc_required.description')}
					checked={settings.kycRequired.checked}
					disabled={settings.kycRequired.disabled}
					onToggle={() => {
						onKycRequiredToggle?.();
						toggleSetting('kycRequired');
					}}
				/>
			</KycSection>

			{settings.kycRequired.checked && (
				<MantineTabs
					tabs={tabsConfig}
					panels={[]}
					onlyDividerBorder={true}
					value={activeTab}
					onTabChange={(value) => setActiveTab(value as ENTITY_TABS)}
				>
					<KycSection $noMaxWidth>
						{activeTab === ENTITY_TABS.INDIVIDUAL ? (
							<>
								<SectionTitle>PROOFS</SectionTitle>
								<ToggleRequirementItem
									title={tMipManage('kyc_settings.proofs.identity.label')}
									description={tMipManage(
										'kyc_settings.proofs.identity.description'
									)}
									checked={settings.proofOfIdentityRequired.checked}
									disabled={settings.proofOfIdentityRequired.disabled}
									onToggle={() => toggleSetting('proofOfIdentityRequired')}
									showBorder={false}
								/>
								{certificationEnabled &&
									settings.proofOfIdentityRequired.checked && (
										<NestedRequirement>
											<ToggleRequirementItem
												title={tMipManage(
													'kyc_settings.certification.required.label'
												)}
												description={tMipManage(
													'kyc_settings.certification.required.description'
												)}
												checked={
													settings.proofOfIdentityCertificationRequired
														.checked
												}
												disabled={
													settings.proofOfIdentityCertificationRequired
														.disabled
												}
												onToggle={() =>
													toggleSetting(
														'proofOfIdentityCertificationRequired'
													)
												}
												showBorder={false}
											/>
										</NestedRequirement>
									)}
								<ToggleRequirementItem
									title={tMipManage('kyc_settings.proofs.address.label')}
									description={tMipManage(
										'kyc_settings.proofs.address.description'
									)}
									checked={settings.proofOfAddressRequired.checked}
									disabled={settings.proofOfAddressRequired.disabled}
									onToggle={() => toggleSetting('proofOfAddressRequired')}
									showBorder={false}
								/>
								{certificationEnabled &&
									settings.proofOfAddressRequired.checked && (
										<NestedRequirement>
											<ToggleRequirementItem
												title={tMipManage(
													'kyc_settings.certification.required.label'
												)}
												description={tMipManage(
													'kyc_settings.certification.required.description'
												)}
												checked={
													settings.proofOfAddressCertificationRequired
														.checked
												}
												disabled={
													settings.proofOfAddressCertificationRequired
														.disabled
												}
												onToggle={() =>
													toggleSetting(
														'proofOfAddressCertificationRequired'
													)
												}
												showBorder={false}
											/>
										</NestedRequirement>
									)}
								<SectionDivider />
								<SectionTitle>DECLARATIONS</SectionTitle>
								<ToggleRequirementItem
									title={tMipManage('kyc_settings.declarations.label')}
									description={tMipManage(
										'kyc_settings.declarations.description'
									)}
									checked={settings.beneficialOwnershipRequired.checked}
									disabled={settings.beneficialOwnershipRequired.disabled}
									onToggle={() => toggleSetting('beneficialOwnershipRequired')}
									showBorder={false}
								/>
								<ToggleRequirementItem
									title={tMipManage('kyc_settings.source.label')}
									description={tMipManage('kyc_settings.source.description')}
									checked={settings.sourceOfFundsRequired.checked}
									disabled={settings.sourceOfFundsRequired.disabled}
									onToggle={() => toggleSetting('sourceOfFundsRequired')}
									showBorder={false}
								/>
								<ToggleRequirementItem
									title={tMipManage('kyc_settings.political.label')}
									description={tMipManage('kyc_settings.political.description')}
									checked={settings.pepRequired.checked}
									disabled={settings.pepRequired.disabled}
									onToggle={() => toggleSetting('pepRequired')}
									showBorder={false}
								/>
							</>
						) : (
							<>
								<SectionTitle>PROOFS</SectionTitle>
								<ToggleRequirementItem
									title={tMipManage('kyc_settings.proofs.vehicle.label')}
									description={tMipManage(
										'kyc_settings.proofs.vehicle.description'
									)}
									checked={settings.proofOfInvestmentVehicleRequired.checked}
									disabled={settings.proofOfInvestmentVehicleRequired.disabled}
									onToggle={() =>
										toggleSetting('proofOfInvestmentVehicleRequired')
									}
									showBorder={false}
								/>
								{certificationEnabled &&
									settings.proofOfInvestmentVehicleRequired.checked && (
										<NestedRequirement>
											<ToggleRequirementItem
												title={tMipManage(
													'kyc_settings.certification.required.label'
												)}
												description={tMipManage(
													'kyc_settings.certification.required.description'
												)}
												checked={
													settings
														.proofOfInvestmentVehicleCertificationRequired
														.checked
												}
												disabled={
													settings
														.proofOfInvestmentVehicleCertificationRequired
														.disabled
												}
												onToggle={() =>
													toggleSetting(
														'proofOfInvestmentVehicleCertificationRequired'
													)
												}
												showBorder={false}
											/>
										</NestedRequirement>
									)}
								<SectionDivider />
								<SectionTitle>DECLARATIONS</SectionTitle>
								<ToggleRequirementItem
									title={tMipManage('kyc_settings.benefical.label')}
									description={tMipManage('kyc_settings.benefical.description')}
									checked={settings.beneficialOwnershipRequired.checked}
									disabled={settings.beneficialOwnershipRequired.disabled}
									onToggle={() => toggleSetting('beneficialOwnershipRequired')}
									showBorder={false}
								/>
							</>
						)}
					</KycSection>
				</MantineTabs>
			)}
		</Drawer>
	);
};
